import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Button,
    Col,
    Form, Icon, Input, Modal, Row, Typography, Upload,
} from 'antd';
import {FormattedMessage, injectIntl} from 'react-intl';
import * as PropTypes from 'prop-types';
import {connect, useSelector} from 'react-redux';
import {
    selectEditState,
    selectLocationsPageDataRequest, selectSubmitLocationRequest,
} from '../../../../state/ui/configuration/location/locationUIReducer';
import { selectLocation } from '../../../../state/domain/locations/locationReducer';
import {
    cancelEditLocation, requestAddLocation, requestUpdateLocation,
} from '../../../../state/ui/configuration/location/locationUiActions';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import AddressInput from "../../../../components/fields/AddressInput";

const { Text } = Typography;

const blankAddress = {
    line1: '',
    line2: '',
    line3: '' ,
    line4: '',
    region: '',
    zipPostcode: '',
    country: undefined,
};

// FIELDS
const DescriptionField = injectIntl(({
                                         form, location, intl, checkForDuplicates,
                                     }) => (
    <Form.Item required label={<FormattedMessage id="locationName" />} style={{paddingTop: '20px'}}>
        { form.getFieldDecorator('locationDescription', {
            initialValue: location ? location.locationDescription : undefined,
            rules: [
                { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
                {
                    validator(rule, value, callback) {
                        if (value !== (location ? location.locationDescription : '')
                            && checkForDuplicates(location ? location.locationCode : null, value)) {
                            callback(intl.formatMessage({ id: 'Duplicate' }));
                        }
                        callback();
                    },
                },
            ],
        })(
            <Input
                type="text"
                maxLength={100}
                allowClear
                placeholder={intl.formatMessage({ id: 'enter' })}
            />,
        )}
    </Form.Item>
));

const TradingNameField = injectIntl(({
                                          form, location, intl,
                                      }) => (
    <Form.Item label={<FormattedMessage id="tradingName" />}>
        { form.getFieldDecorator('tradingName', {
            initialValue: location ? location.tradingName : undefined,
            rules: [
            ],
        })(
            <Input
                type="text"
                maxLength={250}
                allowClear
                placeholder={intl.formatMessage({ id: 'enter' })}
            />,
        )}
    </Form.Item>
));

const ContactEmailField = injectIntl(({
                                         form, location, intl,
                                     }) => (
    <Form.Item label={<FormattedMessage id="email" />}>
        { form.getFieldDecorator('contactEmail', {
            initialValue: location ? location.contactEmail : undefined,
            rules: [
                { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
            ],
        })(
            <Input
                type="email"
                maxLength={80}
                allowClear
                placeholder={intl.formatMessage({ id: 'enter' })}
            />,
        )}
    </Form.Item>
));

// Creates an address form item, decorator is address, returns an object matching address.
const AddressField = injectIntl(({
                          initialValue,
                          className,
                          label,
                          form,
                          required,
                          countries,
                          getCountries,
                          intl,
                          clearAddress,
                          ...props
                     }) => {
    const [validationStatus, setValidationStatus] = useState({});
    return (
        <Form.Item label={label} extra={<Button onClick={clearAddress}><FormattedMessage id="clear" /></Button>} className={className} validateStatus="success" {...props}>
            { form.getFieldDecorator('addressDetails', {
                initialValue: initialValue || {},
                rules: [
                    {
                        validator(rule, value, callback) {
                            let newValidationStatus = {};

                            if (value.line1 || value.line2 || value.line3 || value.line4 || value.region || value.zipPostcode || value.country) {
                                if (!value.country) {
                                    callback(true);
                                    newValidationStatus = {
                                        ...newValidationStatus,
                                        country: intl.formatMessage({id: 'addCountryRequired'})
                                    };
                                }

                                if (!value.line1) {
                                    callback(true);
                                    newValidationStatus = {
                                        ...newValidationStatus,
                                        line1: intl.formatMessage({id: 'addLine1Required'})
                                    };
                                }
                            }

                            setValidationStatus(newValidationStatus);
                            callback();
                        },
                    },
                ],
            })(
                <AddressInput validationStatus={validationStatus} />,
            )}
        </Form.Item>
    )
});



// MODAL AND FORM
const EditLocation = injectIntl(Form.create({ name: 'edit-location' })(({
                            intl, form, visible, setVisible, location, onCreate, onUpdate, descValidator,
                            onCancel, isLoading, isSubmitting, validationBox, companyName, companyEmail, ...rest
                        }) => {
    const isCreating = !location;
    const titleId = isCreating ? 'createLocation' : 'updateLocation';
    const buttonId = 'save';

    const { resetFields } = form;

    // React State
    const [fileList, setFileList] = useState([]); // A list of files. Only has at most 1 item. (logo)
    const [showInfo, setShowInfo] = useState(true); // Info message control

    // Redux State
    const updateRequest = useSelector(selectSubmitLocationRequest);
    const updateLoading = updateRequest ? updateRequest.isLoading : false;

    // Reset fields when it becomes visible.
    useEffect(() => {
        if (visible){
            resetFields();
            setFileList([]);
        }
    }, [resetFields, visible]);

    // Update the icon 'list' to have the
    useEffect(() => {
        if (!location || !location.logoType) return;

        setFileList([{
            uid: location.locationDescription,
            name: location.locationDescription,
            status: 'done',
            contentType: location.logoType,
            url: `data:${location.logoType};base64,${location.logoData}`,
        }]);
    }, [location, setFileList, intl]);

    // Show info message
    useEffect(() => {
        setShowInfo(isCreating || !(location && location.tradingName && location.addressDetails && location.logoData && location.contactEmail));
    }, [isCreating, location]);

    // On form submit.
    const submitForm = useCallback(() => {
        form.validateFields((err, values) => {
            if (!err) {
                const newLocation = location
                    ? { ...location, ...values }
                    : { active: true, ...values };
                if (newLocation.addressDetails != null && newLocation.addressDetails === blankAddress) {
                    newLocation.addressDetails = null;
                    newLocation.addressCode = null;   //TODO leaving orphaned address records but if do not set to null here then remains on record which may cause confusion (and address delete was failing)
                }
                newLocation.tradingName = newLocation.tradingName ? newLocation.tradingName : null;
                newLocation.contactEmail = newLocation.contactEmail ? newLocation.contactEmail : null;
                newLocation.companyName = newLocation.companyName ? newLocation.companyName : companyName;
                newLocation.companyEmail = newLocation.companyEmail ? newLocation.companyEmail : companyEmail;

                const submitFunction = isCreating ? onCreate : onUpdate;
                submitFunction(newLocation);
            }
        });
    }, [form, location, isCreating, onCreate, onUpdate, companyName, companyEmail]);

    return (
        <Modal
            title={<FormattedMessage id={titleId} />}
            okText={<FormattedMessage id={buttonId} />}
            onOk={submitForm}
            visible={visible}
            onCancel={() => {
                form.setFieldsValue({addressDetails: blankAddress});
                form.validateFields();
                onCancel();
            }}
            confirmLoading={isSubmitting}
            {...rest}
        >
            <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
                {
                    showInfo ? (<Alert type="info" description={<FormattedMessage id="locationInfoMessage" />} showIcon />) : ''
                }
                <ViaValidationAlert validationError={validationBox} />
                <DescriptionField form={form} location={location} checkForDuplicates={descValidator} />
                <TradingNameField form={form} location={location} />
                <Row>
                    <Form.Item label={<FormattedMessage id="logo" />}>
                        { form.getFieldDecorator('logoData', {
                            initialValue: location && location.logoData,
                            rules: [
                                {
                                    validator(rule, value, callback) {
                                        const logoType = form.getFieldValue('logoType');
                                        if (logoType && !['image/jpg', 'image/jpeg', 'image/png'].find((type) => type === logoType)) {
                                            callback(intl.formatMessage({ id: 'fileMustHaveAnAcceptedContentType' }, { 0: '[image/jpg, image/jpeg, image/png]' }));
                                        }
                                        callback();
                                    },
                                },
                            ],
                        })(
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Upload
                                    className="avatar-uploader"
                                    accept=".png,.jpg,.jpeg"
                                    showUploadList={false}
                                    listType="text"
                                    fileList={fileList}
                                    beforeUpload={(file) => {
                                        const reader = new FileReader();
                                        reader.onload = (e) => {
                                            const dataUrl = e.target.result;
                                            // sets the logo data and datatype
                                            form.setFieldsValue({ logoData: dataUrl.slice(dataUrl.indexOf(',') + 1), logoType: dataUrl.slice(dataUrl.indexOf(':') + 1, dataUrl.indexOf(';')) });
                                            form.validateFields(['logoData']);
                                        };
                                        // reads the data of the file into a string of style "data:{contentType};base64,{fileDataInBase64}"
                                        reader.readAsDataURL(file);
                                        return false; // Prevent upload
                                    }}
                                    onChange={(info) => { setFileList(info.fileList.slice(-1)); }} // Updates the file list
                                    onRemove={() => form.setFieldsValue({ logoData: null, logoType: null })} // removes the logo
                                >
                                    {fileList.length !== 0
                                        ? (
                                            <img src={`data:${form.getFieldValue('logoType')};base64,${form.getFieldValue('logoData')}`} alt="" className="via-fake-upload" style={{ cursor: 'pointer' }} />
                                        )
                                        : (
                                            <Row className="via-fake-upload" style={{ padding: '5px', cursor: 'pointer' }}>
                                                <Col span={24}>
                                                    <Icon type={updateLoading ? 'loading' : 'plus'} style={{ fontSize: '30px', marginTop: '5px' }} />
                                                </Col>
                                                <Col span={24}>
                                                    <Text>{intl.formatMessage({ id: 'upload' })}</Text>
                                                </Col>
                                            </Row>
                                        )}
                                </Upload>
                                {fileList.length !== 0
                                    ? <Button style={{ marginTop: '0.3em', width: '120px' }} onClick={() => { form.setFieldsValue({ logoData: null, logoType: null }); setFileList([]); }}><FormattedMessage id="remove" /></Button>
                                    : <></>}
                            </div>,
                        )}
                    </Form.Item>
                    <Form.Item style={{ height: '0', marginBottom: '0', marginTop: '0' }}>
                        {
                            // antd refuses to let me add arbitrary form fields without a relevant render.
                            // this creates a dud field for the logo type since in reality it is part of the upload field above
                            form.getFieldDecorator('logoType', { initialValue: location && location.logoType, rules: [] })(<div />)
                        }
                    </Form.Item>
                </Row>
                <Row>
                    <Row>
                        <AddressField form={form} initialValue={location && location.addressDetails ? location.addressDetails : blankAddress}
                                      required
                                      label={<FormattedMessage id="address" />}
                                      clearAddress={() => {
                                          form.setFieldsValue({addressDetails: blankAddress});
                                          form.validateFields();
                                      }} />
                    </Row>
                </Row>
                <ContactEmailField form={form} location={location} />
            </Form>
        </Modal>
    );
}));

// Validate the prop type parameters.
EditLocation.propTypes = {
    visible: PropTypes.bool.isRequired,
    location: PropTypes.shape({
        active: PropTypes.bool.isRequired,
        locationDescription: PropTypes.string.isRequired,
        tradingName: PropTypes.string,
        emailContact: PropTypes.string,
        addressCode: PropTypes.number,
        addressDetails: PropTypes.shape({
            line1: PropTypes.string,
            line2: PropTypes.string,
            line3: PropTypes.string,
            line4: PropTypes.string,
            region: PropTypes.string,
            zipPostcode: PropTypes.string,
            country: PropTypes.string,
        }),
        logoData: PropTypes.string,
        logoType: PropTypes.string,
    }),
    onCreate: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    validationBox: PropTypes.shape({
        errorMessage: PropTypes.string,
        errors: PropTypes.arrayOf(PropTypes.string),
    }),
};

EditLocation.defaultProps = {
    location: null,
    isLoading: false,
    checklists: [],
    validationBox: null,
};

const mapStateToProps = (state) => {
    const editState = selectEditState(state);
    const submitRequest = selectSubmitLocationRequest(state);
    const pageDataRequest = selectLocationsPageDataRequest(state);

    return {
        visible: editState.isEditing,
        location: editState.locationCode ? selectLocation(state, editState.locationCode) : null,
        isSubmitting: submitRequest ? submitRequest.isLoading : false,
        isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
        validationBox: submitRequest ? submitRequest.error : undefined,
    };
};

const mapDispatchToProps = dispatch => ({
    onCreate: location => dispatch(requestAddLocation(location)),
    onUpdate: location => dispatch(requestUpdateLocation(location)),
    onCancel: () => dispatch(cancelEditLocation()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditLocation);
